import { Component, ElementRef, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core'
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { IonIcon, IonModal } from '@ionic/angular/standalone'
import { TranslatePipe } from '@ngx-translate/core'
import { addIcons } from 'ionicons'
import { close } from 'ionicons/icons'
import { finalize, Subject, takeUntil } from 'rxjs'

import { HelperService } from '../../../../services/helper.service'
import { quickBetValueValidator } from '../../../../validators/quick-bet-value.validator'
import { ToastService } from '../../../toast/toast.service'
import { TOAST_STATE } from '../../../toast/types/toast.type'
import { EditStakeModalService } from './edit-stake-modal.service'

@Component({
	selector: 'app-edit-stake-modal',
	templateUrl: './edit-stake-modal.component.html',
	styleUrls: ['./edit-stake-modal.component.scss'],
	standalone: true,
	imports: [ReactiveFormsModule, TranslatePipe, IonModal, IonIcon],
	encapsulation: ViewEncapsulation.None,
})
export class EditStakeModalComponent implements OnInit, OnDestroy {
	isSubmitting = false

	private readonly destroy$ = new Subject<void>()

	editFixedStakeValuesForm = new FormGroup({
		quickBetAmounts: new FormArray(
			[
				new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
				new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
				new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
				new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
				new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
				new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
				new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
			],
			{ validators: [quickBetValueValidator()] },
		),
	})

	constructor(
		protected readonly editStakeModalService: EditStakeModalService,
		private readonly elementRef: ElementRef,
		private readonly helperService: HelperService,
		private readonly toastService: ToastService,
	) {
		addIcons({ close })
	}

	get formControls() {
		return this.editFixedStakeValuesForm.controls
	}

	get quickBetAmountControls() {
		return this.formControls.quickBetAmounts.controls
	}

	formatPriceLabel(value: string): string {
		const numericValue = Number(value)

		if (!Number.isNaN(numericValue) && numericValue >= 1000 && numericValue % 1000 === 0) {
			return `${numericValue / 1000}k`
		}

		return value
	}

	ngOnInit() {
		// Move this component to the document's body tag
		document.body.appendChild(this.elementRef.nativeElement)

		// Initialize the inputs with the passed down values
		this.editFixedStakeValuesForm.patchValue({
			quickBetAmounts: this.editStakeModalService.quickBetAmounts$.getValue(),
		})
	}

	ngOnDestroy() {
		this.destroy$.next()
		this.destroy$.complete()
	}

	/**
	 * Submit the updated stake list
	 */
	submitForm() {
		if (this.editFixedStakeValuesForm.valid) {
			this.isSubmitting = true
			const data = this.editFixedStakeValuesForm.value.quickBetAmounts as string[]
			const userData = this.helperService.getUserData()

			this.editStakeModalService
				.updateQuickBetAmounts(data!)
				.pipe(
					finalize(() => (this.isSubmitting = false)),
					takeUntil(this.destroy$),
				)
				.subscribe({
					next: (response) => {
						if (response.status === 'ok') {
							this.editStakeModalService.quickBetAmounts$.next(data)
							if (userData?.user_fields?.USER_BETSLIP) {
								this.helperService.setUserData({
									...userData,
									user_fields: { ...userData?.user_fields, USER_BETSLIP: { ...userData.user_fields.USER_BETSLIP, quickBetAmounts: data } },
								})
							}

							this.editStakeModalService.dismissQuickBetAmountsEditModal()
						} else {
							this.toastService.showToast(TOAST_STATE.MEDIUM, 'Unexpected error has occurred')
						}
					},
				})
		}
	}
}
